import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { bomberosMallaAPI, bomberosPersonalAPI } from '../../lib/api';
import { LOGO_CBS_BASE64 } from '../../assets/logoCbsBase64';
import { BomberosHeader } from '../../components/BomberosHeader';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    ArrowLeft,
    Download,
    FileText,
    Monitor,
    BookOpen,
    ChevronRight,
    Settings,
    Search,
    Users,
    CheckCircle,
    XCircle,
    User,
    Loader2,
    X,
    CalendarIcon,
    BarChart3,
} from 'lucide-react';

const NIVEL_CONFIG = {
    postulante: { label: 'Nivel Postulante', color: '#6B7280', bgClass: 'bg-gray-700' },
    inicial: { label: 'Nivel Inicial', color: '#2563EB', bgClass: 'bg-blue-600' },
    operativo: { label: 'Nivel Operativo', color: '#DC2626', bgClass: 'bg-red-600' },
    profesional: { label: 'Nivel Profesional', color: '#1E3A5F', bgClass: 'bg-slate-800' },
};

const ESPEC_CONFIG = {
    especialidad_rescate: { label: 'Rescate', short: 'Rescate' },
    especialidad_mat_peligrosos: { label: 'Mat. Peligrosos', short: 'MatPel' },
    especialidad_incendios: { label: 'Incendios', short: 'Incendios' },
    especialidad_material_mayor: { label: 'Material Mayor', short: 'MatMayor' },
    especialidad_adm_emergencias: { label: 'Adm. Emergencias', short: 'AdmEmerg' },
    especialidad_gestion_riesgos: { label: 'Gestión de Riesgos', short: 'GestRiesg' },
    especialidad_hazmat: { label: 'HazMat', short: 'HazMat' },
    especialidad_telecomunicaciones: { label: 'Telecomunicaciones', short: 'Telecom' },
    especialidad_mandos: { label: 'Mandos', short: 'Mandos' },
};

const BomberosDetalleByCompania = ({ data }) => {
    const allBomberos = [...(data.completados || []), ...(data.pendientes || [])];
    const completadosSet = new Set((data.completados || []).map(b => b.id));
    // Group by compañía
    const grouped = {};
    allBomberos.forEach(b => {
        const cia = b.compania || 'Sin compañía';
        if (!grouped[cia]) grouped[cia] = { completados: [], pendientes: [] };
        if (completadosSet.has(b.id)) grouped[cia].completados.push(b);
        else grouped[cia].pendientes.push(b);
    });
    const companias = Object.keys(grouped).sort();
    return (
        <div className="space-y-4">
            {companias.map(cia => (
                <div key={cia}>
                    <p className="text-xs font-bold text-foreground mb-2 border-b border-border pb-1">{cia}</p>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <p className="text-[11px] font-semibold text-green-600 mb-1.5 flex items-center gap-1">
                                <CheckCircle className="h-3.5 w-3.5" /> Cursado ({grouped[cia].completados.length})
                            </p>
                            <div className="space-y-1">
                                {grouped[cia].completados.length > 0 ? grouped[cia].completados.map(b => (
                                    <div key={b.id} className="text-xs py-1 px-2 rounded bg-green-50 dark:bg-green-950/30 text-green-800 dark:text-green-300">
                                        {b.nombre}
                                    </div>
                                )) : <p className="text-xs text-muted-foreground italic">Ninguno</p>}
                            </div>
                        </div>
                        <div>
                            <p className="text-[11px] font-semibold text-red-500 mb-1.5 flex items-center gap-1">
                                <XCircle className="h-3.5 w-3.5" /> No cursado ({grouped[cia].pendientes.length})
                            </p>
                            <div className="space-y-1">
                                {grouped[cia].pendientes.length > 0 ? grouped[cia].pendientes.map(b => (
                                    <div key={b.id} className="text-xs py-1 px-2 rounded bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300">
                                        {b.nombre}
                                    </div>
                                )) : <p className="text-xs text-muted-foreground italic">Ninguno</p>}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
            {companias.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">No hay bomberos asignados a esta malla</p>}
        </div>
    );
};

export default function MallaCurricularPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [anio, setAnio] = useState('2025');
    const [mallaData, setMallaData] = useState(null);
    const mallaRef = useRef(null);

    // Seguimiento por bombero
    const [bomberos, setBomberos] = useState([]);
    const [selectedBombero, setSelectedBombero] = useState(null);
    const [searchBombero, setSearchBombero] = useState('');
    const [showBomberoSearch, setShowBomberoSearch] = useState(false);
    const [progreso, setProgreso] = useState(null);
    const [savingProgreso, setSavingProgreso] = useState(false);

    // Detalle bomberos por curso (click en malla general)
    const [cursoDetalle, setCursoDetalle] = useState(null);
    const [cursoDetalleData, setCursoDetalleData] = useState(null);
    const [loadingDetalle, setLoadingDetalle] = useState(false);
    const [loadingInforme, setLoadingInforme] = useState(false);

    // Modal opciones informe global
    const [showInformeModal, setShowInformeModal] = useState(false);
    const [informeFechaInicio, setInformeFechaInicio] = useState(null);
    const [informeFechaFin, setInformeFechaFin] = useState(null);
    const [informePreset, setInformePreset] = useState('todo');

    useEffect(() => {
        loadBomberos();
    }, []);

    useEffect(() => {
        loadMalla();
    }, [anio]);

    useEffect(() => {
        if (selectedBombero) loadProgreso();
        else setProgreso(null);
    }, [selectedBombero, anio]);

    const loadBomberos = async () => {
        try {
            const res = await bomberosPersonalAPI.getAll({ activo: true });
            setBomberos(res.data);
        } catch (err) { /* ignore */ }
    };

    const loadMalla = async () => {
        try {
            setLoading(true);
            const res = await bomberosMallaAPI.getCursos(anio);
            setMallaData(res.data);
        } catch (err) {
            toast.error('Error al cargar malla curricular');
        } finally {
            setLoading(false);
        }
    };

    const loadProgreso = async () => {
        if (!selectedBombero) return;
        try {
            const res = await bomberosMallaAPI.getProgreso(selectedBombero.id, anio);
            setProgreso(res.data);
        } catch (err) {
            toast.error('Error al cargar progreso');
        }
    };

    const handleToggleCurso = async (cursoMallaId, completado) => {
        if (!selectedBombero) return;
        try {
            setSavingProgreso(true);
            await bomberosMallaAPI.updateProgreso({
                bombero_id: selectedBombero.id,
                curso_malla_id: cursoMallaId,
                completado: !completado,
            });
            await loadProgreso();
        } catch (err) {
            toast.error('Error al actualizar');
        } finally {
            setSavingProgreso(false);
        }
    };

    const getProgresoMap = () => {
        if (!progreso?.cursos) return {};
        const map = {};
        progreso.cursos.forEach(c => { map[c.id] = c.estado_progreso || (c.completado ? 'completado' : 'pendiente'); });
        return map;
    };

    const handleCursoClick = async (curso) => {
        // Solo funciona en vista general (sin bombero seleccionado)
        if (selectedBombero) return;
        if (cursoDetalle?.id === curso.id) {
            setCursoDetalle(null);
            setCursoDetalleData(null);
            return;
        }
        setCursoDetalle(curso);
        setLoadingDetalle(true);
        try {
            const res = await bomberosMallaAPI.getBomberosByCurso(curso.id, anio);
            setCursoDetalleData(res.data);
        } catch (err) {
            toast.error('Error al cargar detalle');
        } finally {
            setLoadingDetalle(false);
        }
    };

    const NIVEL_LABELS = { postulante: 'Postulante', inicial: 'Inicial', operativo: 'Operativo', profesional: 'Profesional' };
    const NIVEL_COLORS_CHART = { postulante: '#374151', inicial: '#2563EB', operativo: '#DC2626', profesional: '#1E3A5F' };

    const handleInformeGlobal = async () => {
        setLoadingInforme(true);
        try {
            const params = {};
            if (informeFechaInicio) params.fecha_inicio = format(informeFechaInicio, 'yyyy-MM-dd');
            if (informeFechaFin) params.fecha_fin = format(informeFechaFin, 'yyyy-MM-dd');

            const res = await bomberosMallaAPI.getInformeGlobal(anio, params);
            const d = res.data;
            const r = d.resumen;
            const fechaHoy = new Date().toLocaleDateString('es-CL', { day: '2-digit', month: 'long', year: 'numeric' });

            let periodoLabel = '';
            if (informeFechaInicio && informeFechaFin) {
                periodoLabel = `${format(informeFechaInicio, 'dd/MM/yyyy')} — ${format(informeFechaFin, 'dd/MM/yyyy')}`;
            } else if (informeFechaInicio) {
                periodoLabel = `Desde ${format(informeFechaInicio, 'dd/MM/yyyy')}`;
            } else if (informeFechaFin) {
                periodoLabel = `Hasta ${format(informeFechaFin, 'dd/MM/yyyy')}`;
            } else {
                periodoLabel = `A&ntilde;o ${anio} completo`;
            }

            const ESTADO_COLORS = { finalizada: '#16A34A', programada: '#2563EB', en_curso: '#D97706', cancelada: '#DC2626' };
            const ESTADO_LABELS = { finalizada: 'Finalizada', programada: 'Programada', en_curso: 'En Curso', cancelada: 'Cancelada' };

            const renderPctBar = (pct, color = '#16A34A') => `<div style="display:flex;align-items:center;gap:6px"><div style="flex:1;height:8px;background:#e2e8f0;border-radius:4px;overflow:hidden"><div style="height:100%;background:${color};border-radius:4px;width:${pct}%"></div></div><span style="font-size:10px;font-weight:700;color:${color};min-width:32px;text-align:right">${pct}%</span></div>`;

            const renderBarCompania = (c) => {
                const total = c.completados + c.inscritos + c.pendientes;
                if (total === 0) return '<div style="height:24px;background:#f1f5f9;border-radius:4px;text-align:center;font-size:10px;line-height:24px;color:#94a3b8">Sin datos</div>';
                const pComp = (c.completados / total * 100).toFixed(0);
                const pInsc = (c.inscritos / total * 100).toFixed(0);
                const pPend = (c.pendientes / total * 100).toFixed(0);
                return `<div style="display:flex;height:24px;border-radius:4px;overflow:hidden;font-size:10px;font-weight:600;color:white;line-height:24px;text-align:center">
                    ${c.completados > 0 ? `<div style="width:${pComp}%;background:#16A34A;min-width:28px">${c.completados}</div>` : ''}
                    ${c.inscritos > 0 ? `<div style="width:${pInsc}%;background:#EAB308;min-width:28px;color:#422006">${c.inscritos}</div>` : ''}
                    ${c.pendientes > 0 ? `<div style="width:${pPend}%;background:#DC2626;min-width:28px">${c.pendientes}</div>` : ''}
                </div>`;
            };

            const renderGauge = (pct, color, size) => {
                const rad = size / 2 - 4;
                const circ = 2 * Math.PI * rad;
                const offset = circ - (pct / 100) * circ;
                return `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
                    <circle cx="${size/2}" cy="${size/2}" r="${rad}" fill="none" stroke="#e2e8f0" stroke-width="6"/>
                    <circle cx="${size/2}" cy="${size/2}" r="${rad}" fill="none" stroke="${color}" stroke-width="6" stroke-dasharray="${circ}" stroke-dashoffset="${offset}" stroke-linecap="round" transform="rotate(-90 ${size/2} ${size/2})"/>
                    <text x="${size/2}" y="${size/2}" text-anchor="middle" dominant-baseline="central" font-size="${size > 60 ? 16 : 12}" font-weight="800" fill="${color}">${pct}%</text>
                </svg>`;
            };

            const formatFecha = (f) => {
                if (!f) return '-';
                const parts = f.split('T')[0];
                const [y, m, dd] = parts.split('-');
                return y && m && dd ? `${dd}/${m}/${y}` : parts;
            };

            const renderTopMalla = (top, mallaAnio, totalBomberos) => {
                if (!top || top.length === 0) return `<p style="color:#94a3b8;font-size:10px;padding:8px">Sin datos para Malla ${mallaAnio}</p>`;
                return `<table>
                    <tr><th>#</th><th>Nombre</th><th>Compa&ntilde;&iacute;a</th><th>Cursos</th><th style="width:120px">% Cumplimiento</th></tr>
                    ${top.map((b, i) => `<tr><td style="font-weight:700;color:#64748b">${i+1}</td><td style="font-weight:600">${b.nombre}</td><td>${b.compania}</td><td>${b.completados}/${b.total}</td><td>${renderPctBar(b.porcentaje)}</td></tr>`).join('')}
                </table>
                <div style="font-size:9px;color:#94a3b8;text-align:right;margin-top:4px">${totalBomberos} bomberos asignados a Malla ${mallaAnio}</div>`;
            };

            const pw = window.open('', '_blank');
            pw.document.write(`<!DOCTYPE html><html><head>
            <title>Informe Global ${anio}</title>
            <style>
                *{margin:0;padding:0;box-sizing:border-box}
                body{font-family:'Segoe UI',Arial,sans-serif;background:white;color:#1a1a1a;font-size:11px}
                .header{background:#1E3A5F;color:white;padding:18px 28px;display:flex;align-items:center;gap:16px;border-bottom:4px solid #DC2626}
                .header img{width:55px;height:55px}
                .header-text{flex:1}
                .header h1{font-size:20px;font-weight:800;letter-spacing:1.5px}
                .header .sub{font-size:11px;opacity:.85;margin-top:2px}
                .header .periodo{font-size:10px;opacity:.7;margin-top:3px;font-style:italic}
                .content{padding:18px 28px}
                .section{margin-bottom:18px}
                .section-title{font-size:12px;font-weight:800;text-transform:uppercase;letter-spacing:1px;color:#1E3A5F;border-bottom:2px solid #1E3A5F;padding-bottom:4px;margin-bottom:10px}
                .kpi-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:14px}
                .kpi{border:1.5px solid #e2e8f0;border-radius:8px;padding:10px 12px;text-align:center}
                .kpi .num{font-size:22px;font-weight:800;line-height:1.1}
                .kpi .label{font-size:9px;color:#64748b;margin-top:2px;text-transform:uppercase;letter-spacing:.5px}
                .kpi.green .num{color:#16A34A} .kpi.blue .num{color:#2563EB} .kpi.red .num{color:#DC2626} .kpi.amber .num{color:#D97706}
                table{width:100%;border-collapse:collapse;font-size:10px}
                th{background:#f1f5f9;padding:6px 8px;text-align:left;font-weight:700;font-size:9px;text-transform:uppercase;letter-spacing:.5px;color:#475569;border-bottom:2px solid #e2e8f0}
                td{padding:5px 8px;border-bottom:1px solid #f1f5f9;vertical-align:middle}
                tr:nth-child(even) td{background:#fafafa}
                .estado-badge{display:inline-block;padding:2px 8px;border-radius:10px;font-size:9px;font-weight:700;color:white}
                .cia-bar{margin-bottom:8px}
                .cia-bar .cia-name{font-size:10px;font-weight:600;margin-bottom:2px;color:#334155}
                .nivel-cards{display:grid;grid-template-columns:repeat(4,1fr);gap:8px}
                .nivel-card{border-radius:8px;overflow:hidden;border:1.5px solid #e2e8f0;text-align:center}
                .nivel-card .hdr{color:white;padding:6px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:1px}
                .nivel-card .body{padding:10px 8px}
                .nivel-card .body .cursos{font-size:9px;color:#64748b;margin-top:4px}
                .leyenda{display:flex;gap:16px;font-size:9px;margin-top:6px;color:#64748b}
                .leyenda span{display:flex;align-items:center;gap:3px}
                .leyenda .dot{width:10px;height:10px;border-radius:2px;display:inline-block}
                .dual-top{display:grid;grid-template-columns:1fr 1fr;gap:20px}
                .dual-top .top-section{border:1.5px solid #e2e8f0;border-radius:8px;overflow:hidden}
                .dual-top .top-header{padding:8px 12px;font-size:11px;font-weight:700;color:white;text-transform:uppercase;letter-spacing:1px}
                .dual-top .top-body{padding:8px}
                .footer{margin-top:16px;padding:10px 16px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:6px;display:flex;justify-content:space-between;align-items:center;font-size:9px;color:#64748b}
                .footer img{width:24px;height:24px;margin-right:6px}
                @media print{body{padding:0;-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important;color-adjust:exact!important}
                    .section{page-break-inside:avoid}}
            </style></head><body>

            <div class="header">
                <img src="${LOGO_CBS_BASE64}" alt="CBS"/>
                <div class="header-text">
                    <h1>INFORME GLOBAL DE CAPACITACI&Oacute;N</h1>
                    <div class="sub">Cuerpo de Bomberos Salamanca &mdash; A&ntilde;o ${anio}</div>
                    <div class="periodo">Per&iacute;odo: ${periodoLabel}</div>
                </div>
            </div>

            <div class="content">
                <!-- 1. RESUMEN GENERAL -->
                <div class="section">
                    <div class="section-title">Resumen General</div>
                    <div class="kpi-grid">
                        <div class="kpi blue"><div class="num">${r.capacitaciones_anio}</div><div class="label">Cursos Realizados</div></div>
                        <div class="kpi green"><div class="num">${r.finalizadas}</div><div class="label">Finalizados</div></div>
                        <div class="kpi amber"><div class="num">${r.programadas}</div><div class="label">Programados</div></div>
                        <div class="kpi red"><div class="num">${r.canceladas}</div><div class="label">Cancelados</div></div>
                    </div>
                    <div class="kpi-grid">
                        <div class="kpi blue"><div class="num">${r.total_bomberos_activos}</div><div class="label">Bomberos Activos</div></div>
                        <div class="kpi green"><div class="num">${r.bomberos_capacitados}</div><div class="label">Bomberos Capacitados</div></div>
                        <div class="kpi amber"><div class="num">${r.horas_cursos.toLocaleString()}</div><div class="label">Horas Cursos</div></div>
                        <div class="kpi red"><div class="num">${r.horas_hombre.toLocaleString()}</div><div class="label">Horas-Hombre Acumuladas</div></div>
                    </div>
                </div>

                <!-- 2. ASISTENCIA POR COMPAÑÍA -->
                <div class="section">
                    <div class="section-title">Bomberos por Compa&ntilde;&iacute;a Asistentes a Cursos</div>
                    <table>
                        <tr><th>Compa&ntilde;&iacute;a</th><th style="text-align:center">Total Bomberos</th><th style="text-align:center">Asistieron</th><th style="text-align:center">Asistencias Totales</th><th style="width:140px">% Participaci&oacute;n</th></tr>
                        ${d.companias_asistencia.map(c => `<tr>
                            <td style="font-weight:600">${c.nombre}</td>
                            <td style="text-align:center">${c.total_bomberos}</td>
                            <td style="text-align:center;font-weight:700;color:#16A34A">${c.asistieron}</td>
                            <td style="text-align:center;font-weight:600">${c.asistencias_totales}</td>
                            <td>${renderPctBar(c.porcentaje_participacion)}</td>
                        </tr>`).join('')}
                    </table>
                </div>

                <!-- 3. CUMPLIMIENTO MALLA POR COMPAÑÍA (barras apiladas) -->
                <div class="section">
                    <div class="section-title">Cumplimiento Malla ${anio} por Compa&ntilde;&iacute;a</div>
                    ${(d.companias_malla || []).map(c => `<div class="cia-bar"><div class="cia-name">${c.nombre} (${c.total} bomberos)</div>${renderBarCompania(c)}</div>`).join('')}
                    <div class="leyenda">
                        <span><span class="dot" style="background:#16A34A"></span> Completado</span>
                        <span><span class="dot" style="background:#EAB308"></span> En Progreso</span>
                        <span><span class="dot" style="background:#DC2626"></span> Pendiente</span>
                    </div>
                    <table style="margin-top:8px">
                        <tr><th>Compa&ntilde;&iacute;a</th><th>Bomberos</th><th>Completados</th><th>En Progreso</th><th>Pendientes</th><th style="width:120px">% Cumplimiento</th></tr>
                        ${(d.companias_malla || []).map(c => `<tr><td style="font-weight:600">${c.nombre}</td><td>${c.total}</td><td style="color:#16A34A;font-weight:600">${c.completados}</td><td style="color:#D97706;font-weight:600">${c.inscritos}</td><td style="color:#DC2626;font-weight:600">${c.pendientes}</td><td>${renderPctBar(c.porcentaje_promedio)}</td></tr>`).join('')}
                    </table>
                </div>

                <!-- 4. DETALLE POR NIVEL (gauges circulares) -->
                <div class="section">
                    <div class="section-title">Detalle por Nivel &mdash; Malla ${anio}</div>
                    <div class="nivel-cards">
                        ${['postulante','inicial','operativo','profesional'].map(n => {
                            const niv = (d.niveles || {})[n] || {total_cursos: 0, porcentaje: 0};
                            return `<div class="nivel-card">
                                <div class="hdr" style="background:${NIVEL_COLORS_CHART[n]}">${NIVEL_LABELS[n]}</div>
                                <div class="body">${renderGauge(niv.porcentaje, NIVEL_COLORS_CHART[n], 64)}<div class="cursos">${niv.total_cursos} cursos</div></div>
                            </div>`;
                        }).join('')}
                    </div>
                </div>

                <!-- 5. TOP 10 CURSOS MALLA -->
                <div class="section">
                    <div class="section-title">Top 10 Cursos con Mayor Cumplimiento &mdash; Malla ${anio}</div>
                    <table>
                        <tr><th>#</th><th>Curso</th><th>Nivel</th><th>Completados</th><th style="width:140px">% Cumplimiento</th></tr>
                        ${(d.top_cursos || []).map((c, i) => `<tr><td style="font-weight:700;color:#64748b">${i+1}</td><td>${c.nombre}</td><td><span style="display:inline-block;padding:1px 6px;border-radius:8px;font-size:9px;font-weight:600;background:${(NIVEL_COLORS_CHART[c.nivel] || '#64748b')}22;color:${NIVEL_COLORS_CHART[c.nivel] || '#64748b'}">${NIVEL_LABELS[c.nivel] || c.nivel}</span></td><td style="font-weight:600">${c.completados}</td><td>${renderPctBar(c.porcentaje)}</td></tr>`).join('')}
                    </table>
                </div>

                <!-- 6. TOP 10 POR MALLA (2024 y 2025 lado a lado) -->
                <div class="section">
                    <div class="section-title">Top 10 Bomberos por Malla Curricular</div>
                    <div class="dual-top">
                        <div class="top-section">
                            <div class="top-header" style="background:#374151">Malla 2024</div>
                            <div class="top-body">${renderTopMalla(d.top_malla_2024, '2024', d.total_bomberos_malla_2024)}</div>
                        </div>
                        <div class="top-section">
                            <div class="top-header" style="background:#1E3A5F">Malla 2025</div>
                            <div class="top-body">${renderTopMalla(d.top_malla_2025, '2025', d.total_bomberos_malla_2025)}</div>
                        </div>
                    </div>
                </div>

                <!-- 7. DETALLE CAPACITACIONES -->
                <div class="section">
                    <div class="section-title">Detalle de Capacitaciones del A&ntilde;o</div>
                    <table>
                        <tr><th>Curso</th><th>Estado</th><th>Fecha Inicio</th><th style="text-align:center">Horas</th><th style="text-align:center">Asistentes</th><th style="text-align:center">Inscritos</th></tr>
                        ${d.capacitaciones.map(c => `<tr>
                            <td style="font-weight:600">${c.nombre}</td>
                            <td><span class="estado-badge" style="background:${ESTADO_COLORS[c.estado] || '#64748b'}">${ESTADO_LABELS[c.estado] || c.estado}</span></td>
                            <td>${formatFecha(c.fecha_inicio)}</td>
                            <td style="text-align:center">${c.horas}</td>
                            <td style="text-align:center;font-weight:700;color:#16A34A">${c.estado === 'cancelada' ? '-' : (c.asistentes || '-')}</td>
                            <td style="text-align:center">${c.inscritos}</td>
                        </tr>`).join('')}
                    </table>
                </div>

                <!-- FOOTER -->
                <div class="footer">
                    <div style="display:flex;align-items:center"><img src="${LOGO_CBS_BASE64}" alt=""/>Cuerpo de Bomberos Salamanca</div>
                    <div>Generado el ${fechaHoy} &mdash; ${periodoLabel} | Malla ${anio} (${d.total_cursos_malla || 0} cursos, ${d.total_cursos_obligatorios || 0} obligatorios) | ${d.capacitaciones.length} capacitaciones registradas</div>
                </div>
            </div>
            </body></html>`);
            pw.document.close();
            setTimeout(() => pw.print(), 600);
            setShowInformeModal(false);
        } catch (err) {
            console.error(err);
            toast.error('Error al generar informe');
        } finally {
            setLoadingInforme(false);
        }
    };

    const handleOpenInformeModal = () => {
        setInformeFechaInicio(null);
        setInformeFechaFin(null);
        setInformePreset('todo');
        setShowInformeModal(true);
    };

    const applyPreset = (preset) => {
        setInformePreset(preset);
        const now = new Date();
        const year = parseInt(anio);
        if (preset === 'todo') {
            setInformeFechaInicio(null);
            setInformeFechaFin(null);
        } else if (preset === 'q1') {
            setInformeFechaInicio(new Date(year, 0, 1));
            setInformeFechaFin(new Date(year, 2, 31));
        } else if (preset === 'q2') {
            setInformeFechaInicio(new Date(year, 3, 1));
            setInformeFechaFin(new Date(year, 5, 30));
        } else if (preset === 'q3') {
            setInformeFechaInicio(new Date(year, 6, 1));
            setInformeFechaFin(new Date(year, 8, 30));
        } else if (preset === 'q4') {
            setInformeFechaInicio(new Date(year, 9, 1));
            setInformeFechaFin(new Date(year, 11, 31));
        } else if (preset === 's1') {
            setInformeFechaInicio(new Date(year, 0, 1));
            setInformeFechaFin(new Date(year, 5, 30));
        } else if (preset === 's2') {
            setInformeFechaInicio(new Date(year, 6, 1));
            setInformeFechaFin(new Date(year, 11, 31));
        } else if (preset === 'custom') {
            // no reset, user picks dates
        }
    };

    const handleExportPDF = () => {
        const niveles = mallaData?.niveles || {};
        const pMap = getProgresoMap();
        const nivelKeys = ['postulante', 'inicial', 'operativo', 'profesional'];
        const activeNiveles = nivelKeys.filter(n => (niveles[n] || []).length > 0);
        const especKeys = Object.keys(niveles).filter(k => k.startsWith('especialidad_'));
        const compKey = niveles.complementario?.length > 0 ? 'complementario' : null;

        const NIVEL_COLORS = {
            postulante:  { bg: '#374151', light: '#F3F4F6', border: '#D1D5DB', text: '#1F2937' },
            inicial:     { bg: '#2563EB', light: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF' },
            operativo:   { bg: '#DC2626', light: '#FEF2F2', border: '#FECACA', text: '#991B1B' },
            profesional: { bg: '#1E3A5F', light: '#F0F4F8', border: '#C7D7E8', text: '#1E3A5F' },
        };

        const fechaHoy = new Date().toLocaleDateString('es-CL', { day: '2-digit', month: 'long', year: 'numeric' });

        const renderEstadoIcon = (est) => {
            if (est === 'completado') return '<span class="icon-circle icon-ok">&#10004;</span>';
            if (est === 'inscrito') return '<span class="icon-circle icon-enrolled">&mdash;</span>';
            return '<span class="icon-circle icon-pending">&#10008;</span>';
        };

        const renderCursoRow = (c, idx) => {
            const est = pMap[c.id] || 'pendiente';
            const zebraClass = idx % 2 === 0 ? 'row-even' : 'row-odd';
            return `<tr class="${zebraClass}">
                ${selectedBombero ? `<td class="td-icon">${renderEstadoIcon(est)}</td>` : ''}
                <td class="td-nombre">${c.nombre}${est === 'inscrito' && selectedBombero ? ' <span class="tag-inscrito">inscrito</span>' : ''}</td>
            </tr>`;
        };

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`<!DOCTYPE html><html><head>
            <title>Malla Curricular ${anio}${selectedBombero ? ' - ' + selectedBombero.nombre_completo : ''}</title>
            <style>
                * { margin: 0; padding: 0; box-sizing: border-box; }
                body { font-family: 'Segoe UI', Arial, sans-serif; background: white; color: #1a1a1a; font-size: 11px; }

                /* === HEADER INSTITUCIONAL === */
                .header-banner {
                    background: #1E3A5F;
                    color: white;
                    padding: 18px 32px 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 18px;
                    border-bottom: 4px solid #DC2626;
                }
                .header-logo { width: 60px; height: 60px; flex-shrink: 0; }
                .header-text { text-align: center; }
                .header-banner h1 {
                    font-size: 26px;
                    font-weight: 800;
                    letter-spacing: 2px;
                    margin-bottom: 4px;
                }
                .header-banner .subtitle {
                    font-size: 12px;
                    font-weight: 400;
                    opacity: 0.85;
                    letter-spacing: 0.5px;
                }
                .header-banner .anio-badge {
                    display: inline-block;
                    background: #DC2626;
                    color: white;
                    padding: 2px 14px;
                    border-radius: 12px;
                    font-size: 14px;
                    font-weight: 700;
                    margin-left: 8px;
                    vertical-align: middle;
                }

                .content { padding: 20px 28px 16px; }

                /* === INFO BOMBERO === */
                .bombero-card {
                    background: linear-gradient(135deg, #EFF6FF 0%, #F0F9FF 100%);
                    border: 1.5px solid #93C5FD;
                    border-radius: 8px;
                    padding: 14px 20px;
                    margin-bottom: 18px;
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }
                .bombero-avatar {
                    width: 42px; height: 42px;
                    background: #2563EB;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    color: white; font-weight: 700; font-size: 16px;
                    flex-shrink: 0;
                }
                .bombero-data { flex: 1; }
                .bombero-data .nombre { font-size: 14px; font-weight: 700; color: #0C4A6E; }
                .bombero-data .detalle { font-size: 10px; color: #475569; margin-top: 2px; }
                .bombero-progress { text-align: right; min-width: 160px; }
                .bombero-progress .pct { font-size: 22px; font-weight: 800; color: #0C4A6E; }
                .bombero-progress .pct-label { font-size: 9px; color: #64748B; display: block; }
                .progress-track {
                    background: #E2E8F0; height: 10px; border-radius: 5px;
                    margin-top: 6px; overflow: hidden;
                }
                .progress-fill {
                    height: 10px; border-radius: 5px;
                    background: linear-gradient(90deg, #22C55E 0%, #16A34A 100%);
                    transition: width 0.3s;
                }

                /* === GRID DE NIVELES === */
                .malla-grid {
                    display: flex;
                    gap: 0;
                    margin-bottom: 18px;
                    border-radius: 8px;
                    overflow: hidden;
                    border: 1.5px solid #CBD5E1;
                }
                .nivel-col { flex: 1; min-width: 0; }
                .nivel-col + .nivel-col { border-left: 1.5px solid #CBD5E1; }
                .nivel-header {
                    color: white;
                    padding: 10px 8px;
                    font-weight: 700;
                    font-size: 10px;
                    text-align: center;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }
                .nivel-body {
                    min-height: 100px;
                    padding: 0;
                }
                .nivel-body table {
                    width: 100%;
                    border-collapse: collapse;
                }
                .nivel-body table tr td {
                    padding: 5px 8px;
                    font-size: 9.5px;
                    border-bottom: 1px solid #F1F5F9;
                    vertical-align: middle;
                }
                .row-even { background: white; }
                .row-odd td { background: inherit; }
                .td-icon { width: 22px; text-align: center; padding: 4px 2px !important; }
                .td-nombre { line-height: 1.35; }

                /* Row tinting per level */
                ${activeNiveles.map(n => {
                    const lc = NIVEL_COLORS[n];
                    return `.col-${n} .row-odd { background: ${lc.light}; }`;
                }).join('\n')}

                /* === ICONOS CIRCULARES === */
                .icon-circle {
                    display: inline-flex; align-items: center; justify-content: center;
                    width: 16px; height: 16px; border-radius: 50%;
                    font-size: 9px; font-weight: 700; line-height: 1;
                }
                .icon-ok { background: #DCFCE7; color: #16A34A; border: 1.5px solid #86EFAC; }
                .icon-enrolled { background: #F1F5F9; color: #475569; border: 1.5px solid #CBD5E1; }
                .icon-pending { background: #FEF2F2; color: #DC2626; border: 1.5px solid #FECACA; }

                .tag-inscrito {
                    display: inline-block;
                    background: #F1F5F9; color: #475569;
                    font-size: 7px; font-weight: 600;
                    padding: 1px 5px; border-radius: 8px;
                    vertical-align: middle; margin-left: 3px;
                }

                /* === ESPECIALIDADES === */
                .esp-section {
                    margin-top: 16px;
                    border-radius: 8px;
                    overflow: hidden;
                    border: 1.5px solid #CBD5E1;
                }
                .esp-header-bar {
                    background: #7C3AED;
                    color: white;
                    padding: 10px 14px;
                    font-weight: 700;
                    font-size: 10px;
                    text-transform: uppercase;
                    letter-spacing: 1px;
                }
                .esp-grid {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 0;
                }
                .esp-col {
                    flex: 1;
                    min-width: 140px;
                    border-right: 1px solid #E2E8F0;
                    padding: 0;
                }
                .esp-col:last-child { border-right: none; }
                .esp-col-title {
                    background: #F5F3FF;
                    color: #6B21A8;
                    font-weight: 700;
                    font-size: 9px;
                    padding: 6px 10px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    border-bottom: 1px solid #E2E8F0;
                }
                .esp-col table { width: 100%; border-collapse: collapse; }
                .esp-col table tr td {
                    padding: 4px 8px;
                    font-size: 9px;
                    border-bottom: 1px solid #F1F5F9;
                    vertical-align: middle;
                }
                .esp-col .row-odd { background: #FAFAFE; }

                /* === FOOTER / SIMBOLOGIA === */
                .footer {
                    margin-top: 18px;
                    padding: 10px 16px;
                    background: #F8FAFC;
                    border: 1px solid #E2E8F0;
                    border-radius: 6px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 9px;
                    color: #475569;
                }
                .footer .leyenda { display: flex; gap: 14px; align-items: center; }
                .footer .leyenda-item { display: flex; align-items: center; gap: 4px; }
                .footer .fecha { font-style: italic; }

                @media print {
                    body { padding: 0; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
                    .header-banner { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .nivel-header { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .esp-header-bar { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .icon-circle { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .bombero-card { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .bombero-avatar { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .progress-fill { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .row-odd { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .esp-col-title { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    .footer { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                }
            </style></head><body>

            <!-- HEADER INSTITUCIONAL -->
            <div class="header-banner">
                <img class="header-logo" src="${LOGO_CBS_BASE64}" alt="CBS" />
                <div class="header-text">
                    <h1>MALLA CURRICULAR <span class="anio-badge">${anio}</span></h1>
                    <div class="subtitle">Cuerpo de Bomberos Salamanca &mdash; Plan de Formaci&oacute;n y Capacitaci&oacute;n</div>
                </div>
            </div>

            <div class="content">

            <!-- INFO BOMBERO -->
            ${selectedBombero ? `
                <div class="bombero-card">
                    <div class="bombero-avatar">${(selectedBombero.nombre_completo || '?')[0].toUpperCase()}</div>
                    <div class="bombero-data">
                        <div class="nombre">${selectedBombero.nombre_completo}</div>
                        <div class="detalle">${selectedBombero.rut || ''} &bull; ${selectedBombero.compania_nombre || ''} &bull; Malla ${anio}</div>
                    </div>
                    ${progreso ? `
                    <div class="bombero-progress">
                        <span class="pct">${progreso.porcentaje_obligatorio}%</span>
                        <span class="pct-label">${progreso.completados_obligatorios} de ${progreso.total_obligatorios} obligatorios</span>
                        <div class="progress-track"><div class="progress-fill" style="width:${progreso.porcentaje_obligatorio}%"></div></div>
                    </div>` : ''}
                </div>` : ''}

            <!-- GRID NIVELES OBLIGATORIOS -->
            <div class="malla-grid">
                ${activeNiveles.map((nivel) => {
                    const cfg = NIVEL_CONFIG[nivel];
                    const nc = NIVEL_COLORS[nivel];
                    const cursos = niveles[nivel] || [];
                    return `<div class="nivel-col col-${nivel}">
                        <div class="nivel-header" style="background:${nc.bg}">${cfg.label}</div>
                        <div class="nivel-body">
                            <table>${cursos.map((c, i) => renderCursoRow(c, i)).join('')}</table>
                        </div>
                    </div>`;
                }).join('')}
            </div>

            <!-- ESPECIALIDADES Y COMPLEMENTARIOS -->
            ${compKey || especKeys.length > 0 ? `
            <div class="esp-section">
                <div class="esp-header-bar">Especialidades y Complementarios</div>
                <div class="esp-grid">
                    ${compKey ? `<div class="esp-col">
                        <div class="esp-col-title">Complementarios</div>
                        <table>${niveles.complementario.map((c, i) => renderCursoRow(c, i)).join('')}</table>
                    </div>` : ''}
                    ${especKeys.map(k => `<div class="esp-col">
                        <div class="esp-col-title">${(ESPEC_CONFIG[k] || {label: k}).label}</div>
                        <table>${(niveles[k]||[]).map((c, i) => renderCursoRow(c, i)).join('')}</table>
                    </div>`).join('')}
                </div>
            </div>` : ''}

            <!-- FOOTER -->
            <div class="footer">
                <div class="leyenda">
                    <strong>Simbolog&iacute;a:</strong>
                    ${selectedBombero ? `
                    <span class="leyenda-item"><span class="icon-circle icon-ok">&#10004;</span> Completado</span>
                    <span class="leyenda-item"><span class="icon-circle icon-enrolled">&mdash;</span> Inscrito</span>
                    <span class="leyenda-item"><span class="icon-circle icon-pending">&#10008;</span> Pendiente</span>
                    ` : '<span>Colores = Nivel de formaci&oacute;n</span>'}
                </div>
                <div class="fecha">Generado el ${fechaHoy}</div>
            </div>

            </div>
        </body></html>`);
        printWindow.document.close();
        setTimeout(() => printWindow.print(), 500);
    };

    if (loading) {
        return (
            <>
                <BomberosHeader />
                <div className="space-y-4 p-4 md:p-6 pt-20">
                    <Skeleton className="h-10 w-48" />
                    <Skeleton className="h-96 w-full" />
                </div>
            </>
        );
    }

    const niveles = mallaData?.niveles || {};
    const nivelKeys = ['postulante', 'inicial', 'operativo', 'profesional'];
    const activeNiveles = nivelKeys.filter(n => (niveles[n] || []).length > 0);
    const especKeys = Object.keys(niveles).filter(k => k.startsWith('especialidad_'));
    const compKey = niveles.complementario?.length > 0 ? 'complementario' : null;
    const pMap = getProgresoMap();

    const bomberosFiltrados = bomberos.filter(b => {
        if (!searchBombero) return true;
        const t = searchBombero.toLowerCase();
        return b.nombre_completo?.toLowerCase().includes(t) || b.rut?.toLowerCase().includes(t);
    });

    const CursoItem = ({ curso }) => {
        const estado = pMap[curso.id] || 'pendiente';
        const isExpanded = !selectedBombero && cursoDetalle?.id === curso.id;
        return (
            <div>
                <div
                    className={`flex items-center gap-2 p-1.5 rounded text-xs transition-colors cursor-pointer hover:bg-muted ${selectedBombero && estado === 'completado' ? 'bg-green-50' : ''} ${selectedBombero && estado === 'inscrito' ? 'bg-slate-50' : ''} ${isExpanded ? 'bg-blue-50 ring-1 ring-blue-300' : ''}`}
                    onClick={() => selectedBombero ? handleToggleCurso(curso.id, estado === 'completado') : handleCursoClick(curso)}
                    data-testid={`curso-item-${curso.id}`}
                >
                    {selectedBombero && (
                        estado === 'completado'
                            ? <CheckCircle className="h-3.5 w-3.5 text-green-600 shrink-0" />
                            : estado === 'inscrito'
                                ? <span className="h-3.5 w-3.5 flex items-center justify-center text-slate-500 font-bold shrink-0">—</span>
                                : <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0" />
                    )}
                    <span className={`leading-tight flex-1 ${estado === 'completado' && selectedBombero ? 'text-green-800 font-medium' : estado === 'inscrito' && selectedBombero ? 'text-slate-600' : 'text-foreground'}`}>
                        {curso.nombre}
                    </span>
                    {selectedBombero && estado === 'inscrito' && (
                        <span className="text-[10px] text-slate-500 ml-auto shrink-0">inscrito</span>
                    )}
                    {!selectedBombero && (
                        <span className="text-[10px] text-muted-foreground ml-auto shrink-0">{isExpanded ? '▲' : '▼'}</span>
                    )}
                </div>
                {/* Expandable: bomberos list for this course */}
                {isExpanded && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={(e) => { if (e.target === e.currentTarget) { setCursoDetalle(null); setCursoDetalleData(null); } }}>
                        <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-between px-5 py-3 border-b border-border bg-muted/50">
                                <div>
                                    <p className="text-sm font-bold text-foreground">{curso.nombre}</p>
                                    <p className="text-xs text-muted-foreground">{cursoDetalleData?.total || 0} bomberos con malla {anio}</p>
                                </div>
                                <button onClick={() => { setCursoDetalle(null); setCursoDetalleData(null); }} className="text-muted-foreground hover:text-foreground p-1 rounded-md hover:bg-muted" data-testid="close-curso-detalle">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="overflow-y-auto max-h-[65vh] p-4">
                                {loadingDetalle ? (
                                    <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
                                        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Cargando...
                                    </div>
                                ) : cursoDetalleData ? (
                                    <BomberosDetalleByCompania data={cursoDetalleData} />
                                ) : null}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
        <BomberosHeader />
        <div className="space-y-4 md:space-y-5 p-4 md:p-6 pt-20 md:pt-20">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/bomberos/capacitaciones')} data-testid="back-btn">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div>
                        <h1 className="text-lg sm:text-xl md:text-2xl font-bold flex items-center gap-2 text-foreground">
                            <BookOpen className="h-5 w-5 md:h-6 md:w-6 shrink-0" />
                            Malla Curricular {anio}
                        </h1>
                        <p className="text-xs sm:text-sm text-muted-foreground">
                            {mallaData?.total_cursos || 0} cursos | {mallaData?.total_obligatorios || 0} obligatorios
                        </p>
                    </div>
                </div>
                <div className="flex gap-2 ml-10 sm:ml-0 flex-wrap">
                    <Select value={anio} onValueChange={(v) => { setAnio(v); if (selectedBombero && selectedBombero.malla_asignada !== v) { setSelectedBombero(null); setProgreso(null); } }} disabled={!!selectedBombero}>
                        <SelectTrigger className="w-32" data-testid="select-anio-malla">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="2024">Malla 2024</SelectItem>
                            <SelectItem value="2025">Malla 2025</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button size="sm" variant="outline" onClick={() => navigate('/bomberos/malla-curricular/configurador')} data-testid="configurar-malla-btn">
                        <Settings className="h-4 w-4 mr-2" />
                        Configurar
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleExportPDF} data-testid="export-pdf-malla">
                        <Download className="h-4 w-4 mr-2" />
                        PDF
                    </Button>
                    <Button size="sm" variant="default" onClick={handleOpenInformeModal} disabled={loadingInforme} data-testid="informe-global-btn">
                        {loadingInforme ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                        Informe Global
                    </Button>
                </div>
            </div>

            {/* Filtro por bombero */}
            <Card className="border-2 border-dashed border-blue-200">
                <CardContent className="py-3 px-4">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                        <div className="flex items-center gap-2 shrink-0">
                            <User className="h-4 w-4 text-blue-600" />
                            <span className="text-sm font-medium">Seguimiento:</span>
                        </div>
                        {selectedBombero ? (
                            <div className="flex items-center gap-3 flex-1 flex-wrap">
                                <div className="flex-1 min-w-0">
                                    <span className="font-semibold text-sm">{selectedBombero.nombre_completo}</span>
                                    <span className="text-xs text-muted-foreground ml-2">{selectedBombero.rut} | {selectedBombero.compania_nombre}</span>
                                </div>
                                {progreso && (
                                    <div className="flex items-center gap-2">
                                        <Badge className={progreso.porcentaje_obligatorio === 100 ? 'bg-green-600' : 'bg-amber-500'}>
                                            {progreso.completados_obligatorios}/{progreso.total_obligatorios} obligatorios ({progreso.porcentaje_obligatorio}%)
                                        </Badge>
                                    </div>
                                )}
                                <Button size="sm" variant="ghost" onClick={() => { setSelectedBombero(null); setSearchBombero(''); }} className="text-xs">
                                    Cambiar
                                </Button>
                            </div>
                        ) : (
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Buscar bombero por nombre o RUT para ver su progreso..."
                                    value={searchBombero}
                                    onChange={(e) => { setSearchBombero(e.target.value); setShowBomberoSearch(true); }}
                                    onFocus={() => setShowBomberoSearch(true)}
                                    className="pl-10"
                                    data-testid="search-bombero-malla"
                                />
                                {showBomberoSearch && searchBombero && (
                                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                                        {bomberosFiltrados.slice(0, 10).map(b => (
                                            <div
                                                key={b.id}
                                                className="px-3 py-2 hover:bg-muted cursor-pointer text-sm"
                                                onClick={() => { setSelectedBombero(b); if (b.malla_asignada) setAnio(b.malla_asignada); setShowBomberoSearch(false); setSearchBombero(''); }}
                                            >
                                                <span className="font-medium">{b.nombre_completo}</span>
                                                <span className="text-xs text-muted-foreground ml-2">{b.rut} | {b.compania_nombre} | Malla: {b.malla_asignada || '-'}</span>
                                            </div>
                                        ))}
                                        {bomberosFiltrados.length === 0 && (
                                            <p className="px-3 py-2 text-sm text-muted-foreground">Sin resultados</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Malla Visual */}
            <div ref={mallaRef}>
                {/* Grid de niveles obligatorios */}
                <div className={`grid grid-cols-1 md:grid-cols-${activeNiveles.length} gap-0`}>
                    {activeNiveles.map((nivel, idx) => {
                        const config = NIVEL_CONFIG[nivel];
                        const cursos = niveles[nivel] || [];
                        return (
                            <div key={nivel} className="flex items-stretch" data-testid={`nivel-${nivel}`}>
                                <div className="flex-1 flex flex-col">
                                    <div className={`${config.bgClass} text-white py-3 px-4 text-center font-bold text-xs tracking-wider uppercase ${idx === 0 ? 'rounded-tl-lg' : ''} ${idx === activeNiveles.length - 1 ? 'rounded-tr-lg' : ''}`}>
                                        {config.label}
                                    </div>
                                    <div className={`border-x border-b flex-1 p-2 space-y-0.5 min-h-[180px] bg-background ${idx === 0 ? 'rounded-bl-lg' : ''} ${idx === activeNiveles.length - 1 ? 'rounded-br-lg' : ''}`}>
                                        {cursos.map(curso => <CursoItem key={curso.id} curso={curso} />)}
                                    </div>
                                </div>
                                {idx < activeNiveles.length - 1 && (
                                    <div className="hidden md:flex items-center justify-center px-0.5">
                                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Complementarios y Especialidades */}
                {(compKey || especKeys.length > 0) && (
                    <div className="mt-4" data-testid="nivel-especialidades">
                        <div className="bg-purple-700 text-white py-3 px-4 text-center font-bold text-xs tracking-wider uppercase rounded-t-lg">
                            Especialidades y Cursos Complementarios
                        </div>
                        <div className="border-x border-b rounded-b-lg p-3 bg-background">
                            <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-${Math.min((compKey ? 1 : 0) + especKeys.length, 5)} gap-4`}>
                                {compKey && (
                                    <div>
                                        <p className="text-xs font-bold text-purple-700 mb-2 uppercase tracking-wide">Complementarios</p>
                                        <div className="space-y-0.5">
                                            {niveles.complementario.map(c => <CursoItem key={c.id} curso={c} />)}
                                        </div>
                                    </div>
                                )}
                                {especKeys.map(k => (
                                    <div key={k}>
                                        <p className="text-xs font-bold text-purple-700 mb-2 uppercase tracking-wide">
                                            {(ESPEC_CONFIG[k] || { label: k.replace('especialidad_', '') }).label}
                                        </p>
                                        <div className="space-y-0.5">
                                            {(niveles[k] || []).map(c => <CursoItem key={c.id} curso={c} />)}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {/* Simbologia */}
                <Card className="mt-4">
                    <CardContent className="py-3 px-4">
                        <div className="flex flex-wrap items-center gap-4 text-xs">
                            <span className="font-semibold text-foreground">Simbología:</span>
                            {Object.entries(NIVEL_CONFIG).filter(([k]) => activeNiveles.includes(k)).map(([key, config]) => (
                                <span key={key} className="flex items-center gap-1">
                                    <span className={`w-3 h-3 rounded ${config.bgClass}`}></span>
                                    <span className="text-foreground">{config.label}</span>
                                </span>
                            ))}
                            <span className="flex items-center gap-1 text-purple-700">
                                <span className="w-3 h-3 rounded bg-purple-700"></span>
                                Especialidades
                            </span>
                            {selectedBombero && (
                                <>
                                    <span className="flex items-center gap-1">
                                        <CheckCircle className="h-3 w-3 text-green-600" /> Completado
                                    </span>
                                    <span className="flex items-center gap-1 text-slate-500">
                                        <span className="font-bold">—</span> Inscrito
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <XCircle className="h-3 w-3 text-red-400" /> Pendiente
                                    </span>
                                </>
                            )}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>

        {/* Modal opciones Informe Global */}
        <Dialog open={showInformeModal} onOpenChange={setShowInformeModal}>
            <DialogContent className="sm:max-w-lg" data-testid="informe-global-modal">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5 text-blue-600" />
                        Opciones del Informe Global — Malla {anio}
                    </DialogTitle>
                </DialogHeader>
                <div className="space-y-5 py-2">
                    {/* Presets de período */}
                    <div>
                        <label className="text-sm font-semibold text-foreground mb-2 block">Período de análisis</label>
                        <div className="grid grid-cols-4 gap-2">
                            {[
                                { key: 'todo', label: 'Todo el año' },
                                { key: 'q1', label: 'Q1 (Ene-Mar)' },
                                { key: 'q2', label: 'Q2 (Abr-Jun)' },
                                { key: 'q3', label: 'Q3 (Jul-Sep)' },
                                { key: 'q4', label: 'Q4 (Oct-Dic)' },
                                { key: 's1', label: '1er Semestre' },
                                { key: 's2', label: '2do Semestre' },
                                { key: 'custom', label: 'Personalizado' },
                            ].map(p => (
                                <Button
                                    key={p.key}
                                    size="sm"
                                    variant={informePreset === p.key ? 'default' : 'outline'}
                                    onClick={() => applyPreset(p.key)}
                                    className="text-xs"
                                    data-testid={`informe-preset-${p.key}`}
                                >
                                    {p.label}
                                </Button>
                            ))}
                        </div>
                    </div>

                    {/* Date pickers (visibles en modo custom o cuando hay fechas) */}
                    {(informePreset === 'custom' || informeFechaInicio || informeFechaFin) && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs font-medium text-muted-foreground mb-1 block">Fecha Inicio</label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="w-full justify-start text-left font-normal text-xs" data-testid="informe-fecha-inicio">
                                            <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                                            {informeFechaInicio ? format(informeFechaInicio, 'dd/MM/yyyy') : 'Seleccionar...'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar mode="single" selected={informeFechaInicio} onSelect={(d) => { setInformeFechaInicio(d); setInformePreset('custom'); }} locale={es} />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-muted-foreground mb-1 block">Fecha Fin</label>
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button variant="outline" className="w-full justify-start text-left font-normal text-xs" data-testid="informe-fecha-fin">
                                            <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                                            {informeFechaFin ? format(informeFechaFin, 'dd/MM/yyyy') : 'Seleccionar...'}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar mode="single" selected={informeFechaFin} onSelect={(d) => { setInformeFechaFin(d); setInformePreset('custom'); }} locale={es} />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </div>
                    )}

                    {/* Toggle comparativa eliminado - ahora está integrado en Top por Malla */}

                    {/* Resumen de selección */}
                    <div className="text-xs text-muted-foreground bg-slate-50 dark:bg-slate-900 p-3 rounded-lg">
                        <span className="font-semibold text-foreground">Resumen:</span>{' '}
                        Año {anio}
                        {informeFechaInicio && informeFechaFin ? ` | ${format(informeFechaInicio, 'dd/MM/yyyy')} — ${format(informeFechaFin, 'dd/MM/yyyy')}` : informeFechaInicio ? ` | Desde ${format(informeFechaInicio, 'dd/MM/yyyy')}` : informeFechaFin ? ` | Hasta ${format(informeFechaFin, 'dd/MM/yyyy')}` : ' | Todo el año'}
                        {' | Top 10 Malla 2024 y 2025'}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setShowInformeModal(false)} data-testid="informe-cancel-btn">Cancelar</Button>
                    <Button onClick={handleInformeGlobal} disabled={loadingInforme} data-testid="informe-generar-btn">
                        {loadingInforme ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
                        Generar Informe
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        </>
    );
}
